import { Controller } from '@hotwired/stimulus'
import consumer from '../../channels/consumer'

// Connects to data-controller="sites--site-updates"
export default class extends Controller {
  static values = { siteId: String }
  static targets = ['taskList', 'staleTotals']

  connect() {
    this.subscribeToSite()
  }

  disconnect() {
    this.unsubscribeFromSite()
  }

  subscribeToSite() {
    if (!this.siteIdValue) return

    this.subscription = consumer.subscriptions.create(
      { channel: 'SiteChannel', site_id: this.siteIdValue },
      {
        received: (data) => { this.handleMessage(data) }
      }
    )
  }

  unsubscribeFromSite() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  handleMessage(data) {
    switch (data.action) {
      case 'task_created':
        this.handleTaskCreated(data.site_task_id, data.task_category_id)
        break
      case 'task_updated':
        this.handleTaskUpdated(data.site_task_id)
        break
      case 'task_deleted':
        this.handleTaskDeleted(data.site_task_id)
        break
      case 'tasks_reordered':
        this.reloadTaskList()
        break
    }

    this.showStaleTotals()
  }

  async handleTaskCreated(taskId, taskCategoryId) {
    const taskListEl = document.querySelector('#site_tasks')
    if (!taskListEl) return

    // Skip if the task is already in the DOM (e.g. inserted by Turbo Stream)
    if (document.getElementById(`site_task_${taskId}`)) return

    const categoryContainer = taskCategoryId
      ? document.getElementById(`category_tasks_${taskCategoryId}`)
      : null
    const targetEl = categoryContainer || taskListEl

    try {
      const url = this.buildTaskCardUrl(taskId)
      const html = await this.fetchTaskCard(url)
      if (!html) return

      // Append inside category container, or at end of task list as fallback
      targetEl.insertAdjacentHTML('beforeend', html)

      // Scroll to and highlight the new task
      const newTask = document.getElementById(`site_task_${taskId}`)
      if (newTask) {
        newTask.scrollIntoView({ behavior: 'smooth', block: 'center' })
        newTask.style.animation = 'highlight-fade 2s ease-out'
      }
    } catch (_error) {
      // Task card fetch failed — no action needed
    }
  }

  async handleTaskUpdated(taskId) {
    const existingTask = document.getElementById(`site_task_${taskId}`)
    if (!existingTask) return

    try {
      const url = this.buildTaskCardUrl(taskId)
      const response = await fetch(url, { headers: { 'Accept': 'text/html' } })

      if (!response.ok) return

      const html = await response.text()
      existingTask.outerHTML = html
    } catch (error) {
      // Silently handle errors
    }
  }

  handleTaskDeleted(taskId) {
    const existingTask = document.getElementById(`site_task_${taskId}`)
    if (existingTask) {
      existingTask.remove()
    }
  }

  async fetchTaskCard(url, attempt = 1) {
    const maxAttempts = 3
    const delayMs = 2000

    const response = await fetch(url, { headers: { 'Accept': 'text/html' } })
    if (response.ok) return await response.text()

    // Retry on 404 — task may not be committed yet
    if (response.status === 404 && attempt < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, delayMs))
      return this.fetchTaskCard(url, attempt + 1)
    }

    return null
  }

  buildTaskCardUrl(taskId) {
    const pathParts = window.location.pathname.split('/')
    const projectIdx = pathParts.indexOf('projects')
    const siteIdx = pathParts.indexOf('sites')

    if (projectIdx !== -1 && siteIdx !== -1) {
      const projectId = pathParts[projectIdx + 1]
      const siteId = pathParts[siteIdx + 1]
      return `/projects/${projectId}/sites/${siteId}/site_tasks/${taskId}/card`
    }

    return null
  }

  async reloadTaskList() {
    const taskListEl = document.querySelector('#site_tasks')
    if (!taskListEl) return

    taskListEl.style.opacity = '0.5'

    try {
      // Wait for DB transaction to commit
      await new Promise(resolve => setTimeout(resolve, 1000))

      const url = new URL(window.location.href)
      url.searchParams.set('_ts', Date.now())

      const response = await fetch(url.toString(), {
        headers: { 'Accept': 'text/html' },
        cache: 'no-store'
      })

      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const newTaskList = doc.querySelector('#site_tasks')

      if (newTaskList) {
        taskListEl.innerHTML = newTaskList.innerHTML
      }
    } catch (_error) {
      // Reload failed — no action needed
    } finally {
      taskListEl.style.opacity = '1'
    }
  }

  showStaleTotals() {
    if (this.hasStaleTotalsTarget) {
      this.staleTotalsTarget.classList.remove('d-none')
    }
  }

  refreshTotals() {
    window.location.reload()
  }
}
